import { z } from "zod";

/**
 * Driver wire schemas.
 *
 * `Driver` matches the drivers table joined with users for the name/phone
 * (the dispatcher list needs display name + phone, which live on users).
 * `NewDriver` is the office create payload (POST /drivers).
 */

export const Driver = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  name: z.string(),
  phone: z.string().nullable(),
  isAvailable: z.boolean(),
  isActive: z.boolean(),
  // SPEC-67 §3.8: display-only binding status. `.default()` is LOAD-BEARING
  // (r1 BUG #8): Driver.parse is called on literals that predate binding
  // (POST /drivers at drivers.ts:325, job-detail.ts:159); without defaults
  // those parses throw at runtime. Enforcement never trusts this field.
  deviceBound: z.boolean().default(false),
  deviceBoundAt: z.string().datetime().nullable().default(null),
  // SPEC-68 §3.7: display-only passkey status. `.default()` is LOAD-BEARING
  // (SPEC-67 r1 BUG #8 precedent): Driver.parse is called on literals that
  // predate these columns (routes/drivers.ts:414, lib/job-detail.ts:159);
  // without defaults those parses throw at runtime. Enforcement never trusts
  // these fields — every gate re-reads webauthn_credentials server-side.
  hasPasskey: z.boolean().default(false),
  passkeySynced: z.boolean().nullable().default(null),
  passkeyFallback: z.boolean().default(false),
});
export type Driver = z.infer<typeof Driver>;

export const NewDriver = z.object({
  name: z.string().min(1),
  phone: z.string().nullable().optional(),
  pin: z.string().regex(/^\d{4}$/),
});
export type NewDriver = z.infer<typeof NewDriver>;

/**
 * PATCH /drivers/:id — office PIN management. All fields optional so a single
 * call can unblock, reset the PIN, and/or update display fields. The route
 * rejects an empty body separately (mirrors the reference-data PATCH pattern).
 */
export const UpdateDriver = z
  .object({
    unblockPin: z.literal(true).optional(),
    pin: z.string().regex(/^\d{4}$/).optional(),
    name: z.string().min(1).optional(),
    phone: z.string().nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type UpdateDriver = z.infer<typeof UpdateDriver>;
